import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Download, X, CheckCircle, Smartphone, Monitor, Zap, Shield, Wifi, WifiOff } from 'lucide-react';
import { useSimplePWAInstall } from '../hooks/useSimplePWAInstall';

const InstallPrompt = styled.div<{ $isVisible: boolean; $isMobile: boolean }>`
  position: fixed;
  bottom: ${({ $isMobile }) => ($isMobile ? '20px' : '30px')};
  left: 50%;
  transform: translateX(-50%);
  max-width: ${({ $isMobile }) => ($isMobile ? 'calc(100% - 40px)' : '400px')};
  background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
  color: white;
  padding: ${({ $isMobile }) => ($isMobile ? '16px' : '20px')};
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  transform: ${({ $isVisible }) => $isVisible ? 'translate(-50%, 0)' : 'translate(-50%, 100px)'};
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    max-width: none;
    transform: ${({ $isVisible }) => $isVisible ? 'translateY(0)' : 'translateY(100px)'};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.9);
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
`;

const InstallButton = styled.button<{ $isInstalling: boolean }>`
  background: ${({ $isInstalling }) => 
    $isInstalling ? '#6b7280' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  };
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ $isInstalling }) => $isInstalling ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-top: 8px;
  background: ${({ $type }) => {
    switch ($type) {
      case 'success': return 'rgba(16, 185, 129, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      case 'info': return 'rgba(59, 130, 246, 0.2)';
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'info': return '#3b82f6';
    }
  }};
  border: 1px solid ${({ $type }) => {
    switch ($type) {
      case 'success': return 'rgba(16, 185, 129, 0.3)';
      case 'error': return 'rgba(239, 68, 68, 0.3)';
      case 'info': return 'rgba(59, 130, 246, 0.3)';
    }
  }};
`;

const SmartPWAInstall: React.FC = () => {
  const {
    isInstalled,
    canInstall,
    isIOSDevice,
    isAndroidDevice,
    isMobile,
    showInstallBanner,
    handleInstallClick,
    dismissInstallBanner
  } = useSimplePWAInstall();
  
  const [isInstalling, setIsInstalling] = useState(false);
  const [installStatus, setInstallStatus] = useState<'idle' | 'success' | 'error' | 'info'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Don't show if already installed or if banner is dismissed
  if (isInstalled || !showInstallBanner) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    setInstallStatus('info');
    setStatusMessage('Installing app...');

    try {
      const success = await handleInstallClick();
      
      if (success) {
        setInstallStatus('success');
        setStatusMessage('App installed successfully!');
        setTimeout(() => {
          dismissInstallBanner();
        }, 2000);
      } else {
        if (isIOSDevice) {
          setInstallStatus('info');
          setStatusMessage('For iOS: Tap the share button and select "Add to Home Screen"');
        } else if (isAndroidDevice) {
          setInstallStatus('info');
          setStatusMessage('For Android: Look for the install banner or menu option');
        } else {
          setInstallStatus('info');
          setStatusMessage('Look for the install icon in your browser\'s address bar');
        }
      }
    } catch (error) {
      setInstallStatus('error');
      setStatusMessage('Installation failed. Please try again.');
      console.error('PWA installation error:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const getDeviceIcon = () => {
    if (isMobile) return <Smartphone size={20} />;
    return <Monitor size={20} />;
  };

  const getInstallText = () => {
    if (isInstalling) return 'Installing...';
    if (isIOSDevice) return 'Add to Home Screen';
    if (isAndroidDevice) return 'Install App';
    return 'Install App';
  };

  return (
    <InstallPrompt $isVisible={showInstallBanner} $isMobile={isMobile}>
      <Header>
        <Title>
          {getDeviceIcon()}
          Install FMS App
        </Title>
        <CloseButton onClick={dismissInstallBanner}>
          <X size={18} />
        </CloseButton>
      </Header>

      <Description>
        Get the full FMS experience with offline access, faster performance, and a dedicated app window.
      </Description>

      <Features>
        <Feature>
          <Wifi size={14} />
          Offline Access
        </Feature>
        <Feature>
          <Zap size={14} />
          Faster Loading
        </Feature>
        <Feature>
          <Shield size={14} />
          Secure & Private
        </Feature>
        <Feature>
          <Download size={14} />
          Easy Updates
        </Feature>
      </Features>

      <InstallButton 
        onClick={handleInstall} 
        disabled={isInstalling}
        $isInstalling={isInstalling}
      >
        {isInstalling ? (
          <>
            <div className="animate-spin" style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%' }} />
            Installing...
          </>
        ) : (
          <>
            <Download size={16} />
            {getInstallText()}
          </>
        )}
      </InstallButton>

      {statusMessage && (
        <StatusMessage $type={installStatus}>
          {statusMessage}
        </StatusMessage>
      )}
    </InstallPrompt>
  );
};

export default SmartPWAInstall;
